import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { PasswordStrengthMeterComponent } from '../../ui/password-strength-meter/password-strength-meter.component';
import { ZxcvbnFunction, ZxcvbnResult } from '../../types/zxcvbn.types';
import { SVGIcon, eyeIcon, eyeSlashIcon } from '@progress/kendo-svg-icons';

// Import zxcvbn - using require for compatibility
interface ZxcvbnModule {
  default?: ZxcvbnFunction;
  (password: string): ZxcvbnResult;
}
type ZxcvbnRequireResult = ZxcvbnModule | ZxcvbnFunction;
declare const require: (module: string) => unknown;

let zxcvbn: ZxcvbnFunction;
try {
  const zxcvbnModule = require('zxcvbn') as ZxcvbnRequireResult;
  if (typeof zxcvbnModule === 'function') {
    zxcvbn = zxcvbnModule;
  } else {
    const moduleWithDefault = zxcvbnModule as ZxcvbnModule;
    zxcvbn = (moduleWithDefault.default || moduleWithDefault) as ZxcvbnFunction;
  }
} catch {
  // Fallback if zxcvbn is not available
  const fallbackResult: ZxcvbnResult = { score: 0, feedback: { suggestions: [], warning: '' } };
  zxcvbn = () => fallbackResult;
}

@Component({
  selector: 'app-password-field',
  standalone: true,
  imports: [CommonModule, InputsModule, IconsModule, PasswordStrengthMeterComponent],
  templateUrl: './password-field.component.html',
  styleUrls: ['./password-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordFieldComponent {
  @Input() passwordLabel = 'Password';
  @Input() confirmPasswordLabel = 'Confirm Password';
  @Input() passwordPlaceholder = 'Enter password';
  @Input() confirmPasswordPlaceholder = 'Confirm password';
  @Input() showPasswordStrength = true;
  @Input() required = true;
  @Input() minStrength: number = 3; // Minimum strength score (0-4), default is 3 (Good)

  @Output() passwordChange = new EventEmitter<string>();
  @Output() confirmPasswordChange = new EventEmitter<string>();
  @Output() isValidChange = new EventEmitter<boolean>();

  password = '';
  confirmPassword = '';
  passwordStrength = 0;
  passwordError = '';
  confirmPasswordError = '';
  showPassword = false;
  showConfirmPassword = false;
  eyeIconSvg: SVGIcon = eyeIcon;
  eyeSlashIconSvg: SVGIcon = eyeSlashIcon;

  onPasswordInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value || '';
    this.onPasswordChange(value);
  }

  onPasswordChange(value: string): void {
    this.password = value;
    this.calculateStrength();
    this.validatePassword();
    this.validateConfirmPassword();
    this.passwordChange.emit(this.password);
    this.emitValidity();
  }

  onConfirmPasswordInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value || '';
    this.onConfirmPasswordChange(value);
  }

  onConfirmPasswordChange(value: string): void {
    this.confirmPassword = value;
    this.validateConfirmPassword();
    this.confirmPasswordChange.emit(this.confirmPassword);
    this.emitValidity();
  }

  private calculateStrength(): void {
    if (!this.password) {
      this.passwordStrength = 0;
      return;
    }

    const result = zxcvbn(this.password);
    this.passwordStrength = result.score; // 0-4
  }

  validatePassword(): void {
    this.passwordError = '';
    if (this.required && !this.password) {
      this.passwordError = `${this.passwordLabel} is required`;
      return;
    }
  }

  validateConfirmPassword(): void {
    this.confirmPasswordError = '';
    if (this.required && !this.confirmPassword) {
      this.confirmPasswordError = `${this.confirmPasswordLabel} is required`;
    } else if (this.password && this.confirmPassword && this.password !== this.confirmPassword) {
      this.confirmPasswordError = 'Passwords do not match';
    }
  }

  getStrengthLabel(score: number): string {
    const labels: Record<number, string> = {
      0: 'Very Weak',
      1: 'Weak',
      2: 'Fair',
      3: 'Good',
      4: 'Strong',
    };
    return labels[score] || 'Weak';
  }

  get isPasswordStrong(): boolean {
    return true;
  }

  get isValid(): boolean {
    return (
      (!this.required || (!!this.password && !!this.confirmPassword)) &&
      this.password === this.confirmPassword &&
      this.isPasswordStrong &&
      !this.passwordError &&
      !this.confirmPasswordError
    );
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  private emitValidity(): void {
    this.isValidChange.emit(this.isValid);
  }

  // Public method to get password value
  getPassword(): string {
    return this.password;
  }

  // Public method to reset
  reset(): void {
    this.password = '';
    this.confirmPassword = '';
    this.passwordStrength = 0;
    this.passwordError = '';
    this.confirmPasswordError = '';
    this.emitValidity();
  }
}
